import { useState, useEffect } from "react";
import IntegrationAppCard from "../../../elements/integration_app_card";
import { useAuth } from "../../../context/AuthProvider";
import { BASE_URL } from '../../../apiConfig';

export default function Page() {
  const [integrationApps, setIntegrationApps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
  setError(null);
  
  try {
    const response = await fetch(BASE_URL+'/api/integrations',{
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      setIntegrationApps(result.data);
    } else {
      throw new Error(result.message || 'Failed to fetch integrations');
    }
  } catch (err) {
    console.error('Error fetching integrations:', err);
    setError(err.message);
  } finally {
    setIsLoading(false);
  }
};

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-3">
            Applications d'intégration
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl">
            Chargement des applications d'intégration...
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-md overflow-hidden animate-pulse">
              <div className="bg-gradient-to-br from-gray-200 to-gray-300 p-6 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-gray-300"></div>
              </div>
              <div className="p-4">
                <div className="h-6 bg-gray-300 rounded mb-4 mx-auto w-3/4"></div>
                <div className="space-y-3 mb-4">
                  <div className="h-4 bg-gray-300 rounded w-full"></div>
                  <div className="h-4 bg-gray-300 rounded w-full"></div>
                </div>
                <div className="h-8 bg-gray-300 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-3">
              Applications d'intégration
            </h1>
            <p className="text-sm text-gray-600 max-w-2xl">
              Gérez vos intégrations avec Shopify, YouCan et d'autres applications e-commerce.
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {error && (
              <div className="text-xs text-red-600 bg-red-50 px-3 py-1.5 rounded-lg">
                Erreur de connexion API, affichage des données locales
              </div>
            )}
            
            <button
              onClick={fetchApps}
              className="flex cursor-pointer items-center gap-2 text-xs py-2 px-4 bg-[var(--color-primary-500)] text-white rounded-lg hover:bg-[var(--color-primary-400)] transition-colors duration-300"
            >
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Actualiser
            </button>
          </div>
        </div>
        
        <div className="mt-4 text-xs text-gray-500">
          <p>Total: {integrationApps.length} application{integrationApps.length > 1 ? 's' : ''} • 
            Actives: {integrationApps.filter(a => a.status === 'true').length} • 
            Inactives: {integrationApps.filter(a => a.status === 'false').length}
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-yellow-700">
              {error}. Les données affichées peuvent ne pas être à jour.
            </span>
          </div>
        </div>
      )}

      {/* Cards Grid */}
      {integrationApps.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune application trouvée</h3>
          <p className="text-sm text-gray-500 mb-4">
            Aucune application d'intégration n'est actuellement configurée.
          </p>
          <button
            onClick={fetchApps}
            className="inline-flex items-center gap-2 text-sm py-2 px-4 bg-[var(--color-primary-500)] text-white rounded-lg hover:bg-[var(--color-primary-400)] transition-colors duration-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Réessayer
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-4 gap-8">
          {integrationApps.map((app) => (
            <IntegrationAppCard 
              key={app.name} 
              app={app} 
              fetchApps={fetchApps}
            />
          ))}
        </div>
      )}

    </div>
  );
}

